import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const Imports = lazy(() => import("@/pages/Imports"));
const Receipts = lazy(() => import("@/pages/Receipts"));
const Bills = lazy(() => import("@/pages/Bills"));
const Assistant = lazy(() => import("@/pages/Assistant"));
const Subscriptions = lazy(() => import("@/pages/Subscriptions"));
const Insurance = lazy(() => import("@/pages/Insurance"));
const Household = lazy(() => import("@/pages/Household"));
const Settings = lazy(() => import("@/pages/Settings"));
const CashFlow = lazy(() => import("@/pages/CashFlow"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AdminUsage = lazy(() => import("@/pages/AdminUsage"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const ReceiptsHome = lazy(() => import("@/pages/ReceiptsHome"));
const SharedList = lazy(() => import("@/pages/SharedList"));
const About = lazy(() => import("@/pages/About"));

const PageLoading = () => <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

function Protected({ children }) {
  const { user, configReady } = useAuth();
  if (user === null || !configReady) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  return user ? children : <Navigate to="/login" replace />;
}

const FULL_ONLY = [["cash-flow", CashFlow], ["transactions", Transactions], ["bills", Bills], ["subscriptions", Subscriptions], ["insurance", Insurance]];

function AppRoutes() {
  const { receiptsOnly } = useAuth();
  return (
    <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/s/:token" element={<SharedList />} />
      <Route path="/about" element={<About />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route index element={receiptsOnly ? <ReceiptsHome /> : <Dashboard />} />
        {!receiptsOnly && FULL_ONLY.map(([path, Page]) => <Route key={path} path={path} element={<Page />} />)}
        {!receiptsOnly && <Route path="imports" element={<Imports key="card" kind="card" />} />}
        {!receiptsOnly && <Route path="bank-imports" element={<Imports key="bank" kind="bank" />} />}
        <Route path="receipts" element={<Receipts />} />
        <Route path="ask" element={<Assistant />} />
        <Route path="household" element={<Household />} />
        <Route path="settings" element={<Settings />} />
        <Route path="admin/usage" element={<AdminUsage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
